// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Small domain glyph shown next to an alarm's group/equipment-type, matching the
// icon set used in the mock (cooling, power, electrical, compute, …). Falls back
// to the neutral dot-grid glyph when the group doesn't map to a known domain.

import React from 'react';

type Domain = 'cooling' | 'power' | 'electrical' | 'compute' | 'fire' | 'environment' | 'facility';

/** Best-effort map from a free-text group / equipment-type to a domain glyph. */
function domainFor(group: string | null | undefined): Domain {
  const g = (group ?? '').toLowerCase();
  // The shared domains (utils/assetDomain) first, with the design's glyphs:
  // Controls/BMS and Security take the facility glyph.
  if (/control|bms|security/.test(g)) return 'facility';
  if (/cool|chiller|crah|crac|chw|liquid|mechanical/.test(g)) return 'cooling';
  if (/ups|battery|generator|power|pdu|rpp/.test(g)) return 'power';
  if (/electric|switchgear|breaker|transformer|ats|bus/.test(g)) return 'electrical';
  if (/fire|vesda|smoke|suppress/.test(g)) return 'fire';
  if (/humid|temp|environ|env/.test(g)) return 'environment';
  if (/rack|compute|server|network|bms/.test(g)) return 'compute';
  if (/facil|building|door|access/.test(g)) return 'facility';
  return 'compute';
}

const PATHS: Record<Domain, React.ReactNode> = {
  cooling: (
    <path
      d="M8 1v14M8 1l-1.6 1.6M8 1l1.6 1.6M8 15l-1.6-1.6M8 15l1.6-1.6M1.5 4.75l13 6.5M14.5 4.75l-13 6.5"
      strokeLinecap="round"
    />
  ),
  power: <path d="M9 1L3 9h4l-1 6 7-9H9l1-5z" fill="currentColor" stroke="none" />,
  electrical: (
    <path
      d="M5 2v4M11 2v4M4 6h8v2a4 4 0 01-4 4 4 4 0 01-4-4V6zM8 12v3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  fire: (
    <path
      d="M8 14.5c2.8 0 4.5-1.8 4.5-4.3 0-1.7-.9-2.7-1.7-3.6.2 1-.1 1.8-.7 2.2.2-1.8-.5-3.7-2.4-5.3.3 1.6-.2 2.8-1.3 3.9-1 1-1.9 2-1.9 3.3 0 2.3 1.7 3.8 3.5 3.8z"
      strokeLinejoin="round"
    />
  ),
  environment: (
    <path
      d="M8 1.5s4.5 5 4.5 8.2A4.5 4.5 0 013.5 9.7C3.5 6.5 8 1.5 8 1.5z"
      strokeLinejoin="round"
    />
  ),
  facility: <path d="M3 2.5h10v11H3zM6 5.5h1M9 5.5h1M6 8h1M9 8h1M6 10.5h1M9 10.5h1" />,
  compute: (
    <>
      <circle cx="4.5" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="11.5" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="11.5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="11.5" cy="11.5" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
};

export function GroupIcon({
  group,
  size = 14,
}: {
  group: string | null | undefined;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
    >
      {PATHS[domainFor(group)]}
    </svg>
  );
}

export default GroupIcon;
